import { useState } from 'react'

import { USERS_ACCESS_ANALYTICS } from '../constants/usersAccess'
import type { UsersAccessRole } from '../types/usersAccess'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'

import * as EmailValidator from 'email-validator'

import type { ColorScheme } from '@hivewatch/dark'

import {
  ManagedAccountsDocument,
  PendingInvitesDocument,
  useInviteUserMutation,
  useUpdateUserRolesMutation,
  useUserLazyQuery,
  useUsersLazyQuery,
} from '@/graphql/generated/hooks'
import { trackEvent } from '@/utils/analytics'

import {
  extractExistingRoles,
  findUserIdByEmail,
  mergeRoles,
} from '../utils/helpers'

export interface UsersAccessInviteValuesI {
  email: string
  firstName: string
  isSsoUser: boolean
  lastName: string
  roles: UsersAccessRole[]
}

export type UsersAccessInviteErrors = Partial<
  Record<'email' | 'firstName' | 'lastName' | 'roles', string>
>

const INITIAL_VALUES: UsersAccessInviteValuesI = {
  email: '',
  firstName: '',
  isSsoUser: false,
  lastName: '',
  roles: [],
}

const ASSIGNABLE_ROLE_SET = new Set<UsersAccessRole>([
  'IMPLEMENTER',
  'EMPLOYEE',
  'GUARD',
  'OPERATOR',
  'PROGRAM_MANAGER',
  'READ_ONLY_USER',
])

export const useUsersAccessInvite = (colorScheme: ColorScheme) => {
  const { t } = useTranslation()
  const { hasCRUDPermission, shouldEnableCreateUser } = usePermissions()
  const { showError, showSuccess } = useToasts({
    colorScheme,
    isRebrand: true,
  })
  const [values, setValues] = useState<UsersAccessInviteValuesI>(INITIAL_VALUES)
  const [errors, setErrors] = useState<UsersAccessInviteErrors>({})
  const [fetchUsersByEmail] = useUsersLazyQuery({ fetchPolicy: 'network-only' })
  const [fetchUserById] = useUserLazyQuery({ fetchPolicy: 'network-only' })
  const [inviteUser, { loading: isInviting }] = useInviteUserMutation()
  const [updateUserRoles, { loading: isUpdatingRoles }] =
    useUpdateUserRolesMutation()

  const setField = <FieldT extends keyof UsersAccessInviteValuesI>(
    field: FieldT,
    value: UsersAccessInviteValuesI[FieldT]
  ) => {
    setValues((current) => ({ ...current, [field]: value }))
    setErrors((current) => ({ ...current, [field]: undefined }))
  }

  const toggleRole = (role: UsersAccessRole) => {
    setValues((current) => {
      if (current.roles.includes(role)) {
        return {
          ...current,
          roles: current.roles.filter((value) => value !== role),
        }
      }
      return {
        ...current,
        roles:
          role === 'EMPLOYEE'
            ? ['EMPLOYEE']
            : [...current.roles.filter((value) => value !== 'EMPLOYEE'), role],
      }
    })
    setErrors((current) => ({ ...current, roles: undefined }))
  }

  const reset = () => {
    setValues(INITIAL_VALUES)
    setErrors({})
  }

  const validate = () => {
    const nextErrors: UsersAccessInviteErrors = {}
    if (!values.firstName.trim()) {
      nextErrors.firstName = t('settings:rebrand_users_first_name_required')
    }
    if (!values.lastName.trim()) {
      nextErrors.lastName = t('settings:rebrand_users_last_name_required')
    }
    if (!EmailValidator.validate(values.email.trim())) {
      nextErrors.email = t('settings:rebrand_users_email_invalid')
    }
    if (values.roles.length === 0) {
      nextErrors.roles = t('settings:rebrand_users_roles_required')
    }
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const submit = async () => {
    if (!shouldEnableCreateUser || !validate()) return false

    const normalizedEmail = values.email.trim()
    const selectedRoles = values.roles.filter(
      (role) => role !== 'EMPLOYEE' && ASSIGNABLE_ROLE_SET.has(role)
    )
    trackEvent(USERS_ACCESS_ANALYTICS.inviteSubmit, {
      roles_count: selectedRoles.length,
      is_sso_user: values.isSsoUser,
    })

    try {
      const { data: usersData, error: lookupError } = await fetchUsersByEmail({
        variables: { filter: { email: normalizedEmail } },
      })
      if (lookupError) throw lookupError

      const existingUserId = findUserIdByEmail(usersData, normalizedEmail)
      if (existingUserId) {
        const { data: userData, error: userError } = await fetchUserById({
          variables: { id: existingUserId },
        })
        if (userError) throw userError
        const existingRoles = extractExistingRoles(userData)
        // ENG-33697: the viewer may not act on a user holding roles they
        // cannot manage (an existing Implementer, for a PM) — surface a field
        // error instead of constructing an update the backend will reject
        if (existingRoles.some((role) => !hasCRUDPermission(role))) {
          setErrors((current) => ({
            ...current,
            email: t('settings:rebrand_users_invite_restricted_existing'),
          }))
          return false
        }
        const mergedRoles = mergeRoles(existingRoles, selectedRoles)
        await updateUserRoles({
          variables: {
            input: {
              id: existingUserId,
              roles: mergedRoles,
            },
          },
          refetchQueries: [
            { query: ManagedAccountsDocument },
            { query: PendingInvitesDocument },
          ],
        })
        trackEvent(USERS_ACCESS_ANALYTICS.inviteSuccess, {
          existing_user: true,
          roles_count: selectedRoles.length,
        })
        showSuccess(t('settings:rebrand_users_existing_user_success'))
        reset()
        return true
      }

      await inviteUser({
        variables: {
          input: {
            email: normalizedEmail,
            firstName: values.firstName.trim(),
            isSsoUser: values.isSsoUser,
            lastName: values.lastName.trim(),
            roles: selectedRoles,
          },
        },
        refetchQueries: [
          { query: ManagedAccountsDocument },
          { query: PendingInvitesDocument },
        ],
      })
      trackEvent(USERS_ACCESS_ANALYTICS.inviteSuccess, {
        existing_user: false,
        roles_count: selectedRoles.length,
        is_sso_user: values.isSsoUser,
      })
      showSuccess(t('settings:rebrand_users_invite_success'))
      reset()
      return true
    } catch (error) {
      trackEvent(USERS_ACCESS_ANALYTICS.inviteFailure, {
        existing_user: false,
      })
      Sentry.captureException(error, {
        tags: {
          action: 'invite_user',
          feature: 'managed_accounts',
          surface: 'settings_users_access',
        },
      })
      showError(t('settings:rebrand_users_invite_error'))
      return false
    }
  }

  return {
    errors,
    isLoading: isInviting || isUpdatingRoles,
    reset,
    setField,
    shouldEnableCreateUser,
    submit,
    toggleRole,
    values,
  }
}
